import { useClient } from '@cortex/react-query';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Modal } from '../../common/Modal';

export function OperationStartModal({
  threadId,
  initialObjective,
  onClose,
  onStarted,
}: {
  threadId: string;
  initialObjective: string;
  onClose: () => void;
  onStarted: (operationId: string) => void;
}) {
  const client = useClient();
  const [objective, setObjective] = useState(initialObjective);
  const [stopCondition, setStopCondition] = useState('');
  const [maxWorkers, setMaxWorkers] = useState(10);
  const [recommendScorecard, setRecommendScorecard] = useState(false);
  const start = useMutation({
    mutationFn: async () => {
      try {
        return await client.http
          .post('v1/persistent-agent-operations', {
            json: {
              supervisorAgentId: threadId,
              objective: objective.trim(),
              ...(stopCondition.trim() ? { stopCondition: stopCondition.trim() } : {}),
              maxWorkers,
              scorecard: recommendScorecard ? 'recommend' : 'none',
            },
          })
          .json<{ id: string }>();
      } catch (error) {
        const response = (error as { response?: Response }).response;
        if (response) {
          const body = (await response.json().catch(() => null)) as {
            message?: string | string[];
          } | null;
          const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message;
          if (message) throw new Error(message);
        }
        throw error;
      }
    },
    onSuccess: (operation) => onStarted(operation.id),
  });

  const canStart = objective.trim().length > 0 && !start.isPending;
  return (
    <Modal
      open={true}
      onClose={onClose}
      eyebrow="/operate"
      title="Start supervisor operation"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <button className="btn btn--ghost btn--sm" disabled={start.isPending} onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn--primary btn--sm"
            disabled={!canStart}
            onClick={() => start.mutate()}
          >
            {start.isPending ? 'Starting…' : 'Start operation'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <p className="t-sm t-muted" style={{ margin: 0 }}>
          Creates the durable ledger immediately, then wakes this thread to discover resources,
          staff workers, and keep replanning from operation events.
        </p>
        <label className="field">
          <span className="field-label">Objective</span>
          <textarea
            className="input"
            rows={4}
            autoFocus={true}
            value={objective}
            onChange={(event) => setObjective(event.target.value)}
            placeholder="Keep the external system healthy and resolve incoming work."
          />
        </label>
        <label className="field">
          <span className="field-label">Stop condition (optional)</span>
          <textarea
            className="input"
            rows={2}
            value={stopCondition}
            onChange={(event) => setStopCondition(event.target.value)}
            placeholder="Leave blank for an open-ended operation."
          />
          <span className="field-help">
            Without this, silence or a good score does not stop the operation. The supervisor or an
            operator must decide to pause or stop it.
          </span>
        </label>
        <label className="field" style={{ maxWidth: 220 }}>
          <span className="field-label">Maximum workers</span>
          <input
            className="input"
            type="number"
            min={1}
            max={50}
            value={maxWorkers}
            onChange={(event) =>
              setMaxWorkers(Math.min(50, Math.max(1, Number(event.target.value) || 10)))
            }
          />
          <span className="field-help">Also limited by the current manageWorkers grant.</span>
        </label>
        <label style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={recommendScorecard}
            onChange={(event) => setRecommendScorecard(event.target.checked)}
          />
          <span>
            <span className="t-sm">Configure a scorecard automatically</span>
            <span className="field-help" style={{ display: 'block' }}>
              The supervisor inspects MCP schemas, explains the metrics, and creates the polling
              scorecard. Open-ended work includes a driver that wakes it when action is needed; the
              scorecard never silently becomes the stop condition.
            </span>
          </span>
        </label>
        {start.error && (
          <div className="alert alert--danger" role="alert">
            {(start.error as Error).message}
          </div>
        )}
      </div>
    </Modal>
  );
}
